<template>
  <div class="static-schedule-item qrw-box-shadow-1dp" v-if="!editing">
    <div class="static-item-header">
      <BaseIcon
        :icon="item.is_repeated ? 'refresh-cw' : 'calendar'"
        style="font-size: 28px"
      />
      <div class="qrw-heading-xs qrw-text-content-base-primary w-100">
        {{ formattedStartDate }} |
        <span v-if="startTime && endTime">
          {{ $t("checklist_schedule.from") }} {{ startTime }}
          {{ $t("checklist_schedule.to") }} {{ endTime }}</span
        >
      </div>
    </div>

    <div class="edit-btn">
      <BaseIconButton
        @click="editing = true"
        icon="pencil"
        size="sm"
        variant="ghost"
      />
    </div>
  </div>
  <WhiteIQOS noOverflow bodyClass="checklist-schedule-item-iqos-body" style="overflow: visible" v-else>
    <template #title>
      <div class="schedule-iqos-header">
        <BaseIcon
          :icon="item.is_repeated ? 'refresh-cw' : 'calendar-bold'"
          style="font-size: 28px"
        />
        {{
          item.is_repeated
            ? $t("checklist_schedule.type_repeat")
            : $t("checklist_schedule.type_scheduled")
        }}
      </div>
    </template>

    <template #actions>
      <BaseIconButton
        variant="ghost"
        size="sm"
        icon="trash"
        textColor="content-error-primary"
        @click="$emit('delete')"
      />
      <BasePushButton
        @click="trySave"
        variant="fill"
        size="sm"
        textColor="content-base-inverse-primary"
        bgColor="background-success-primary"
      >
        {{ $t("checklist_schedule.save") }}
      </BasePushButton>
    </template>

    <div class="gap-row-sm">
      <div class="gap-column-xs">
        <div class="gap-row-txs w-100">
          <div class="qrw-label-md qrw-text-content-base-primary">
            {{ $t("checklist_schedule.start_date") }}
          </div>
          <BaseDatepickerInput
            pickerFixedWidth
            size="md"
            v-model="item.base_date"
            placeholder="Select date"
          />
        </div>
        <div class="gap-row-txs w-100">
          <div class="qrw-label-md qrw-text-content-base-primary">
            {{ $t("checklist_schedule.start_time") }}
          </div>
          <BaseTimepickerInput
            :valid="!(invalid && !startTime)"
            v-model="item.start_date"
            placeholder="Select date"
          />
        </div>
        <div class="gap-row-txs w-100">
          <div class="qrw-label-md qrw-text-content-base-primary">
            {{ $t("checklist_schedule.end_time") }}
          </div>
          <BaseTimepickerInput
            :valid="!(invalid && !endTime)"
            v-model="item.end_date"
            placeholder="Select date"
          />
        </div>
      </div>
      <BaseDivider color="border-base-tertiary" />
      <div class="gap-column-1xs">
        <!-- <div class="qrw-heading-xs w-100 bold qrw-text-content-base-primary">

        </div> -->
        <BaseBetaCheckbox v-model="item.is_repeated">
          {{ $t("checklist_schedule.repeatable") }}
        </BaseBetaCheckbox>
      </div>
      <div class="gap-row-txs" v-if="item.is_repeated">
        <div class="qrw-label-md qrw-text-content-base-primary">
          {{ $t("checklist_schedule.repeat_every") }}
        </div>
        <div class="gap-column-txs">
          <BaseNumberInput
            size="md"
            class="repeat-order-input"
            v-model.number="item.repeat_order"
          />
          <BaseTabSwitcher
            size="md"
            v-model="item.repeat_period_unit"
            :tabs="[
              {
                value: 'day',
                text: $t('checklist_schedule.day'),
              },
              {
                value: 'week',
                text: $t('checklist_schedule.week'),
              },
              {
                value: 'month',
                text: $t('checklist_schedule.month'),
              },
              {
                value: 'year',
                text: $t('checklist_schedule.year'),
              },
            ]"
          />
        </div>
        <div class="periods-grid" v-if="periodAllUnits">
          <div
            class="grid-item qrw-label-md"
            @click="togglePeriod(item.value)"
            :class="{
              'active bold qrw-box-shadow-1dp': isPeriodSelected(item.value),
            }"
            v-for="item in periodAllUnits"
            :key="item.value"
          >
            {{ item.text }}
          </div>
        </div>
      </div>
    </div>
  </WhiteIQOS>
</template>

<script>
import moment from "moment";

export default {
  props: {
    item: Object,
  },
  data() {
    return {
      editing: false,
    };
  },
  watch: {
    "item.repeat_period_unit"() {
      this.$set(this.item, "repeat_periods", []);
    },
  },
  methods: {
    trySave() {
      if (!this.startTime || !this.endTime) {
        this.invalid = true;
        return;
      }

      this.editing = false;
      this.invalid = false;
      this.$set(this.item, "__saved", true);
      this.$emit('update');
    },
    isPeriodSelected(value) {
      return this.item.repeat_periods.includes(value);
    },
    togglePeriod(value) {
      if (this.isPeriodSelected(value)) {
        this.item.repeat_periods.splice(
          this.item.repeat_periods.indexOf(value),
          1
        );
      } else {
        this.item.repeat_periods.push(value);
      }

      this.$emit('update');
    },
  },
  computed: {
    formattedStartDate() {
      return moment(this.item.base_date || new Date()).format(
        "dddd, DD.MM.YYYY"
      );
    },
    startTime() {
      if (!this.item.start_date) return null;
      return moment(this.item.start_date).format("HH:mm");
    },
    endTime() {
      if (!this.item.end_date) return null;
      return moment(this.item.end_date).format("HH:mm");
    },
    periodAllUnits() {
      if (this.item.repeat_period_unit === "day") return null;

      if (this.item.repeat_period_unit === "week") {
        return this.$t("checklist_schedule.weekday_names").map(
          (name, index) => ({
            text: name,
            value: index === 6 ? 0 : index + 1,
          })
        );
      }

      if (this.item.repeat_period_unit === "month") {
        const dates = [];

        const count = moment(this.item.base_date).daysInMonth();

        for (let i = 1; i <= count; i++) dates.push(i);

        return dates.map((num, index) => ({
          text: num,
          value: index,
        }));
      }

      if (this.item.repeat_period_unit === "year") {
        return this.$t("checklist_schedule.month_names").map((name, index) => ({
          text: name,
          value: index,
        }));
      }
    },
  },
  mounted() {
    if (
      !(this.item.__saved || this.item._id) ||
      !this.endTime ||
      !this.startTime
    ) {
      this.editing = true;
    }
  },
};
</script>

<style>
.checklist-schedule-item-iqos-body {
  padding: 24px !important;
}
</style>

<style scoped>
.static-schedule-item {
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  border-radius: 8px;
  background: var(--qrw-background-base-primary);
}

.static-schedule-item:hover {
  box-shadow: var(--qrw-shadow-2dp);
}

.static-item-header {
  padding: 12px 0px 12px 16px;
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
}

.edit-btn {
  display: none;

  align-items: center;
  justify-content: center;

  padding: 10px;
  box-sizing: border-box;
}

.static-schedule-item:hover .edit-btn {
  display: flex;
}

.schedule-iqos-header {
  display: flex;
  flex-direction: row;

  align-items: center;
  align-content: center;
  column-gap: 8px;

  box-sizing: border-box;
}

.repeat-order-input {
  width: 120px;
  max-width: 120px;
}

.periods-grid {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  border-radius: 12px;
  background: var(--qrw-background-base-secondary);
  overflow: hidden;
  column-gap: 4px;
  row-gap: 2px;
}

.grid-item {
  padding: 10px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--qrw-content-base-secondary);
  width: calc(14.28% - 4px);
  box-sizing: border-box;
  cursor: pointer;
}

.grid-item.active {
  background: var(--qrw-background-base-inverse-secondary);
  color: var(--qrw-content-base-inverse-primary);
  border-radius: 10px;
}
</style>
